/**
 * Request-detail timeline mapping (WP-F8b) — pure, transport-free, unit-tested.
 *
 * `GET /api/requests/{id}` returns the raw status transitions (snake_case wire statuses). The
 * candidate detail screen surfaces the finer status the ledger deliberately hides (DESIGN-NOTES: the
 * ledger collapses the pipeline band to one `in_progress` card; the detail is where granularity is
 * shown). This module turns each wire transition into ONE warm, candidate-facing milestone line, and
 * computes the resend cooldown gate the server enforces so the button can disable itself with an
 * honest explanation before the round-trip.
 *
 * Copy follows 04-copy voice: warm, plain, second person, no exclamation marks, no invented precision.
 */
import type { ReferenceStatus } from './ledger';

/** The referee's first name for the milestone copy; falls back to "they" when a name is blank. */
export function firstNameOf(name: string | null | undefined): string {
  const first = (name ?? '').trim().split(/\s+/).filter(Boolean)[0];
  return first || 'they';
}

/**
 * The warm milestone label for a wire status (the transition's `toStatus`). `name` is the referee's
 * first name. Unrecognised statuses degrade to a neutral "Status updated" rather than leaking the
 * raw enum — the same conservative stance the ledger mapper takes.
 */
export function milestoneLabel(status: string, name: string): string {
  switch (status as ReferenceStatus) {
    case 'draft':
    case 'requested':
    case 'sent':
      return `Invite sent to ${name}`;
    case 'viewed':
      return `${name} opened your request`;
    case 'accepted':
      return `${name} agreed to help`;
    case 'recorded':
      return `${name} recorded their reference`;
    case 'processing':
      return `Turning ${name}'s words into a letter`;
    case 'drafted':
      return `${name} is reviewing the draft`;
    case 'needs_review':
      return 'Just checking a couple of things';
    case 'rerecord_requested':
      return `${name} is re-recording`;
    case 'failed':
      return 'We hit a snag — we’re on it';
    case 'signed':
      return 'Signed and ready to unlock';
    case 'declined':
      return `${name} couldn’t help this time`;
    case 'expired':
      return 'The invite expired';
    case 'cancelled':
      return 'You withdrew this request';
    default:
      return 'Status updated';
  }
}

/** A rendered timeline milestone — a completed transition with its warm label and timestamp. */
export interface Milestone {
  /** The wire `toStatus`, kept for the key + the dot styling. */
  status: string;
  label: string;
  occurredAt: string;
  /** True for the most recent milestone — the detail screen marks it as the live "you are here". */
  current: boolean;
}

/**
 * Map the wire timeline to warm milestones, newest-last (chronological, oldest → newest, the order
 * a reader follows top-to-bottom). The final entry is flagged `current`.
 */
export function toMilestones(
  timeline: readonly { toStatus: string; occurredAt: string }[],
  refereeName: string,
): Milestone[] {
  const name = firstNameOf(refereeName);
  return timeline.map((entry, i) => ({
    status: entry.toStatus,
    label: milestoneLabel(entry.toStatus, name),
    occurredAt: entry.occurredAt,
    current: i === timeline.length - 1,
  }));
}

/** The server's resend cooldown: an invite can be re-sent 48h after the last send. */
export const RESEND_COOLDOWN_MS = 48 * 60 * 60 * 1000;

/**
 * The states in which nudging the referee again is meaningful — and, more importantly, the states
 * the server will actually re-send from. `ReferenceRequestService.ResendableStates` is
 * `Requested | Sent | Viewed`, which are exactly the candidate-facing `sent` (draft/requested/sent)
 * and `opened` (viewed) states. `expired` is NOT resendable server-side (a re-send needs a live
 * respond edge; an expired invite is terminal), so offering it here only ever earned a 409
 * `not_resendable` — the honest recovery for an expired invite is a fresh request (see the detail
 * screen's expired branch), not a nudge.
 */
const RESENDABLE_STATES = new Set(['sent', 'opened']);

/**
 * Fold a client-tracked last-resend time into the cooldown input. A successful resend never moves
 * the ledger's `sentAt` — the server advances a SEPARATE `last_invite_enqueued_at` timestamp and
 * leaves `sentAt` at the original send — so gating on `sentAt` alone would re-open the button the
 * instant a resend succeeded, and the next click would 429. The detail screen records the moment of
 * a successful resend and passes `max(sentAt, lastResendAt)` here so the gate stays honest.
 */
export function laterIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return ta >= tb ? a : b;
}

export interface ResendGate {
  /** Whether a resend affordance should be shown at all for this card state. */
  applicable: boolean;
  /** Whether the 48h cooldown has elapsed (the button is enabled). */
  open: boolean;
  /** When the cooldown lifts, when it has not yet (for the disabled explanation). */
  availableAt: Date | null;
}

/**
 * Compute the resend gate from the card's ledger state and last-send time. `sentAt` is when the
 * invite last went out; before 48h have elapsed the button is disabled with a "you can nudge again
 * in…" explanation, matching the server's `resend_too_soon` (429) gate. The server stays
 * authoritative — this only prevents an obviously-too-soon round-trip.
 */
export function resendGate(state: string, sentAt: string | null, now: Date = new Date()): ResendGate {
  const applicable = RESENDABLE_STATES.has(state);
  if (!applicable) return { applicable: false, open: false, availableAt: null };
  if (!sentAt) return { applicable: true, open: true, availableAt: null };
  const sent = new Date(sentAt).getTime();
  if (Number.isNaN(sent)) return { applicable: true, open: true, availableAt: null };
  const readyAt = sent + RESEND_COOLDOWN_MS;
  if (now.getTime() >= readyAt) return { applicable: true, open: true, availableAt: null };
  return { applicable: true, open: false, availableAt: new Date(readyAt) };
}

/**
 * The states in which withdrawing (cancel) is offered. The candidate-actor cancel edge in
 * `ReferenceRequestService.CancelAsync` is legal only from `draft | requested | sent` — i.e. the
 * candidate-facing `sent` state. Once the referee has viewed (`opened`), the only cancel edge is the
 * SystemOnly erasure path (WP-B18), which a candidate withdrawal cannot take, so the server answers
 * 409 `cannot_cancel`. Offering withdraw on `opened` therefore only produced a dead control with
 * false "too late — they've already started" copy; it is gated to `sent` alone.
 */
const CANCELLABLE_STATES = new Set(['sent']);

/** Whether the withdraw affordance is shown for a card state (server confirms with 409 otherwise). */
export function canCancel(state: string): boolean {
  return CANCELLABLE_STATES.has(state);
}

/**
 * A coarse, warm "in about …" phrase for a future time — used for the resend cooldown explanation.
 * Rounds up to whole hours (past an hour) or minutes, so the copy never claims false precision.
 */
export function untilPhrase(target: Date, now: Date = new Date()): string {
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return 'shortly';
  const mins = Math.ceil(ms / 60_000);
  if (mins < 60) return `about ${mins} min`;
  const hours = Math.ceil(mins / 60);
  if (hours < 24) return `about ${hours}h`;
  const days = Math.ceil(hours / 24);
  return `about ${days} day${days === 1 ? '' : 's'}`;
}
